import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Settings, Save, Check } from "lucide-react";
import { mediaApi } from "../api";
import { useMediaAuth } from "../contexts/MediaAuthContext";

export default function MediaAdmin() {
  const { user } = useMediaAuth();
  const [items, setItems] = useState([]);
  const [edits, setEdits] = useState({});
  const [saved, setSaved] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    mediaApi.adminContent().then((d) => { setItems(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (user && user.ID_Cliente !== 1) return <Navigate to="/media" replace />;

  const change = (id, field, val) => {
    setEdits((e) => ({ ...e, [id]: { ...(e[id] || {}), [field]: val } }));
  };

  const save = async (id) => {
    const patch = edits[id];
    if (!patch) return;
    try {
      await mediaApi.adminUpdate({ ID_ConteudoMediaCenter: id, ...patch });
      setSaved((s) => ({ ...s, [id]: true }));
      setTimeout(() => setSaved((s) => { const n = { ...s }; delete n[id]; return n; }), 2000);
    } catch {}
  };

  const filtered = items.filter((i) =>
    !filter || (i.Descricao || "").toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="px-4 md:px-8 lg:px-12 py-12 max-w-[1400px] mx-auto"
      data-testid="media-admin-page"
    >
      <div className="flex items-center gap-3 mb-10">
        <Settings className="text-[#D1CFC9]" />
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#D1CFC9] font-bold mb-1">Administração</p>
          <h1 className="font-serif text-5xl font-light">Gestão de Conteúdos</h1>
        </div>
      </div>

      <input
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Pesquisar..."
        className="w-full max-w-md mb-6"
        data-testid="media-admin-filter"
      />

      {loading ? (
        <div className="live-dot" />
      ) : (
        <div className="overflow-x-auto glass-panel rounded-2xl">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wider text-slate-400">
                <th className="py-3 px-4">ID</th>
                <th className="py-3 px-4">Tipo</th>
                <th className="py-3 px-4">ID_Produto</th>
                <th className="py-3 px-4">Descrição</th>
                <th className="py-3 px-4">Conteúdo (URL/Ficheiro)</th>
                <th className="py-3 px-4">Activo</th>
                <th className="py-3 px-4">Ordem</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 200).map((r) => {
                const id = r.ID_ConteudoMediaCenter;
                const e = edits[id] || {};
                return (
                  <tr key={id} className="border-b border-white/5 hover:bg-white/2">
                    <td className="py-2 px-4 text-slate-500">{id}</td>
                    <td className="py-2 px-4 text-[#D1CFC9] font-mono text-xs">{r.ID_TipoConteudoMediaCenter}</td>
                    <td className="py-2 px-4 text-[#D1CFC9] font-mono text-xs" data-testid={`media-admin-prod-${id}`}>
                      {r.ID_Produto ?? <span className="text-slate-600">—</span>}
                    </td>
                    <td className="py-2 px-4 min-w-[200px]">
                      <input
                        defaultValue={r.Descricao || ""}
                        onChange={(ev) => change(id, "Descricao", ev.target.value)}
                        className="w-full !py-1 !px-2 text-sm"
                      />
                    </td>
                    <td className="py-2 px-4 min-w-[240px]">
                      <input
                        defaultValue={r.ConteudoHtml || ""}
                        onChange={(ev) => change(id, "ConteudoHtml", ev.target.value)}
                        className="w-full !py-1 !px-2 text-sm font-mono text-xs"
                      />
                    </td>
                    <td className="py-2 px-4">
                      <select
                        defaultValue={r.Activo}
                        onChange={(ev) => change(id, "Activo", ev.target.value)}
                        className="bg-white/5 border border-white/10 rounded py-1 px-2 text-sm"
                      >
                        <option value="S">S</option>
                        <option value="N">N</option>
                      </select>
                    </td>
                    <td className="py-2 px-4">
                      <input
                        type="number"
                        defaultValue={r.Ordem || 0}
                        onChange={(ev) => change(id, "Ordem", parseInt(ev.target.value) || 0)}
                        className="!py-1 !px-2 text-sm w-20"
                      />
                    </td>
                    <td className="py-2 px-4">
                      {Object.keys(e).length > 0 && (
                        <button
                          onClick={() => save(id)}
                          className="text-[#D1CFC9] hover:text-[#E8E6E2] inline-flex items-center gap-1 text-xs"
                          data-testid={`media-admin-save-${id}`}
                        >
                          {saved[id] ? <><Check size={12} /> Guardado</> : <><Save size={12} /> Guardar</>}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );
}
